import { fetchQuery } from "./sanity";

// Schema-independent probe: proves the connection without knowing document types.
export function getSanityDocuments(limit = 10) {
    const query = `*[!(_id in path("drafts.**"))][0...$limit]{ _id, _type, _createdAt, _updatedAt }`;
    return fetchQuery(query, { limit });
}

// Homepage hero fields only.
export function getHomepageHero() {
    const query = `*[_type == "homepage"][0]{
        heroEyebrow,
        heroHeadline,
        heroSupportingMessage,
        heroPrimaryCta,
        heroSecondaryCta,
        heroImage{ "url": asset->url, alt }
    }`;
    return fetchQuery(query);
}

// Homepage marquee strip (words below the hero).
export function getHomepageMarquee() {
    const query = `*[_type == "homepage"][0]{ marqueeItems }`;
    return fetchQuery(query);
}

// Homepage trust section (headline + supporting message below the marquee).
export function getHomepageTrust() {
    const query = `*[_type == "homepage"][0]{
        trustHeadline,
        trustMessage,
        challenges[]{ _key, title, description }
    }`;
    return fetchQuery(query);
}

export function getHomepageApproach() {
    const query = `*[_type == "homepage"][0]{
        approachHeadline,
        approachChapters[]{ _key, label, title, description }
    }`;
    return fetchQuery(query);
}

export function getHomepageSeo() {
    const query = `*[_type == "homepage"][0]{ seo }`;
    return fetchQuery(query);
}

// Homepage services/capabilities section (headline, intro + resolved featured services).
export function getHomepageServices() {
    const query = `*[_type == "homepage"][0]{
        servicesHeadline,
        servicesIntro,
        "services": featuredServices[]->{
            _id,
            "slug": slug.current,
            name,
            shortDescription
        }
    }`;
    return fetchQuery(query);
}

// Homepage "Why EasiLinked" section (headline, message + differentiators).
export function getHomepageWhy() {
    const query = `*[_type == "homepage"][0]{
        whyEasiLinkedHeadline,
        whyEasiLinkedMessage,
        differentiators[]{ _key, title, description }
    }`;
    return fetchQuery(query);
}

// Homepage closing / final CTA section.
export function getHomepageClosing() {
    const query = `*[_type == "homepage"][0]{
        finalCtaHeadline,
        finalCtaMessage,
        finalCta{ label, url, openInNewTab }
    }`;
    return fetchQuery(query);
}

// Navigation document — primary header CTA + primary nav items.
export function getNavigation() {
    const query = `*[_type == "navigation"][0]{
        primaryCta{ label, url },
        primaryItems[]{ _key, label, url, openInNewTab },
        menuItems[]{ _key, label, url, openInNewTab, children[]{ _key, label, url, openInNewTab } },
        announcementEnabled,
        announcementText,
        announcementUrl
    }`;
    return fetchQuery(query);
}

// Site settings — company name, analytics IDs, and public contact values.
export function getSiteSettings() {
    const query = `*[_type == "siteSettings"][0]{
        companyName,
        legalBusinessName,
        businessDescription,
        websiteUrl,
        linkedinUrl,
        facebookUrl,
        googleAnalyticsId,
        googleTagManagerId,
        primaryPhone,
        primaryEmail
    }`;
    return fetchQuery(query);
}

// All service documents, ordered for the Services overview + detail pages.
const SERVICE_FIELDS = `
    _id,
    name,
    "slug": slug.current,
    shortDescription,
    businessProblem,
    businessOutcome,
    idealClient,
    capabilities,
    benefits,
    featured,
    enterprise,
    healthcareSecure,
    displayOrder,
    cta{ label, url, openInNewTab },
    seo
`;

export function getServices() {
    const query = `*[_type == "service"] | order(displayOrder asc){${SERVICE_FIELDS}}`;
    return fetchQuery(query);
}

export function getServicesPage() {
    const query = `*[_type == "servicesPage"][0]{
        heroEyebrow,
        heroHeadline,
        heroSupportingMessage,
        closingEyebrow,
        closingHeadline,
        closingSupportingMessage,
        closingCta{ label, url, openInNewTab },
        seo
    }`;
    return fetchQuery(query);
}

export function getFooter() {
    const query = `*[_type == "footer"][0]{
        copyrightText,
        footerText,
        tagline,
        quickLinks[]{ _key, label, url },
        legalLinks[]{ _key, label, url },
        showPrimaryCta,
        showSocialLinks
    }`;
    return fetchQuery(query);
}

// Single service resolved by slug for the dynamic detail route.
export function getServiceBySlug(slug) {
    const query = `*[_type == "service" && slug.current == $slug][0]{${SERVICE_FIELDS}}`;
    return fetchQuery(query, { slug });
}

// About page — company narrative + values (featuredPartners intentionally NOT queried in V1).
export function getAboutPage() {
    const query = `*[_type == "aboutPage"][0]{
        heroHeadline,
        heroSupportingMessage,
        companyStoryHeadline,
        companyStory,
        mission,
        vision,
        valuesHeadline,
        values[]{ _key, title, description },
        leadershipHeadline,
        leadershipMessage,
        finalCtaHeadline,
        finalCtaMessage,
        finalCta{ label, url, openInNewTab },
        seo
    }`;
    return fetchQuery(query);
}

// Contact page — hero, contact intro, booking + form copy, visibility booleans.
export function getContactPage() {
    const query = `*[_type == "contactPage"][0]{
        heroHeadline,
        heroSupportingMessage,
        contactIntro,
        showPhone,
        showEmail,
        showAddress,
        bookingHeadline,
        bookingMessage,
        bookingUrl,
        formHeadline,
        formIntro,
        seo
    }`;
    return fetchQuery(query);
}

// Enterprise page — full document with resolved references.
export function getEnterprisePage() {
    const query = `*[_type == "enterprisePage"][0]{
        heroEyebrow,
        heroHeadline,
        heroSupportingMessage,
        heroPrimaryCta{ label, url, openInNewTab },
        heroSecondaryCta{ label, url, openInNewTab },
        challengeHeadline,
        challengeIntro,
        businessChallenges[]{ _key, title, description },
        outcomesHeadline,
        businessOutcomes[]{ _key, title, description },
        servicesHeadline,
        servicesIntro,
        "featuredServices": featuredServices[]->{ _id, name, "slug": slug.current, shortDescription },
        differentiatorHeadline,
        differentiatorMessage,
        differentiators[]{ _key, title, description },
        "featuredFaqs": featuredFaqs[]->{ _id, question, answer },
        finalCtaHeadline,
        finalCtaMessage,
        finalCta{ label, url, openInNewTab },
        seo
    }`;
    return fetchQuery(query);
}

// Healthcare Secure™ page — full document with resolved references.
export function getHealthcarePage() {
    const query = `*[_type == "healthcarePage"][0]{
        heroEyebrow,
        heroHeadline,
        heroSupportingMessage,
        heroPrimaryCta{ label, url, openInNewTab },
        heroSecondaryCta{ label, url, openInNewTab },
        challengeHeadline,
        challengeIntro,
        healthcareChallenges[]{ _key, title, description },
        outcomesHeadline,
        healthcareOutcomes[]{ _key, title, description },
        easiHeadline,
        easiIntro,
        easiSteps[]{ _key, letter, title, description },
        servicesHeadline,
        servicesIntro,
        "featuredServices": featuredServices[]->{ _id, name, "slug": slug.current, shortDescription },
        safeguardsHeadline,
        safeguardsMessage,
        safeguards,
        "featuredFaqs": featuredFaqs[]->{ _id, question, answer },
        finalCtaHeadline,
        finalCtaMessage,
        finalCta{ label, url, openInNewTab },
        seo
    }`;
    return fetchQuery(query);
}

// --- The EasiLinked Perspective -------------------------------------------------
// Hidden until the Perspective page's "Show The Perspective on the Website" switch
// is on (showOnWebsite). Field Notes only get their own page when hasOwnPage is on.

const POST_BASE_FIELDS = `
    _id,
    format,
    title,
    "slug": slug.current,
    excerpt,
    publishedAt,
    featured,
    hasOwnPage,
    "topic": topic->{ title, "slug": slug.current },
    "heroImage": heroImage{ alt, "url": asset->url },
    "originalSource": originalSource{ platform, originalPublishedAt, originalUrl },
    "words": length(string::split(pt::text(body), " "))
`;

const POST_CARD_FIELDS = `${POST_BASE_FIELDS}, "author": author->{ name }`;

export function getPerspectivePage() {
    const query = `*[_id == "perspectivePage"][0]{
        showOnWebsite,
        heroLabel,
        heroHeadline,
        heroSupportingMessage,
        articlesHeadline,
        fieldNotesHeadline,
        fieldNotesIntro,
        closingHeadline,
        closingSupportingMessage,
        closingCta{ label, url, openInNewTab },
        seo
    }`;
    return fetchQuery(query);
}

export function getPerspectiveTopics() {
    const query = `*[_type == "topic"] | order(displayOrder asc){ _id, title, "slug": slug.current }`;
    return fetchQuery(query);
}

// Every published post for the hub. Field Note bodies are included because short
// notes are read in full on the hub.
export function getPerspectivePosts() {
    const query = `*[_type == "post" && defined(slug.current)] | order(publishedAt desc){
        ${POST_CARD_FIELDS},
        "body": select(format == "fieldNote" => body)
    }`;
    return fetchQuery(query);
}

export function getPostBySlug(slug) {
    const query = `*[_type == "post" && slug.current == $slug][0]{
        ${POST_BASE_FIELDS},
        "body": body[]{ ..., _type == "image" => { ..., "url": asset->url } },
        "author": author->{ name, role, bio, linkedinUrl, "photo": photo{ alt, "url": asset->url } },
        "relatedPosts": relatedPosts[]->{ ${POST_CARD_FIELDS} },
        seo
    }`;
    return fetchQuery(query, { slug });
}
